// SPIN CYCLE (§6.5): a washing-machine drum floor with a solid hub, lint-brush sweeper arms and
// soap bubbles. Everything here is a pure function of (seed, round tick, tempo), nothing is
// replicated:
//  - the arms follow a piecewise-linear angle: 1 rev / 4 bars in Calm, 1 rev / 2 bars from Ramp,
//    1 rev / 2 SD bars in Sudden Death, reversing every 4 SD bars (buzzer 2 beats ahead);
//  - the Sudden Death third arm at θ + 90° drops in over the last Ramp bar (its shadow) and lands
//    on the Sudden Death downbeat;
//  - the bubbles drift on seeded Lissajous paths and pop on bar lines after 2 beats of wobble.
// Sweeps and pops go through `hazardKick` / `hazardNudge`: full hits are server-only, and the local
// bean's velocity is predicted (§18.2), so riding an arm feels the same online as offline.
import { TICK_HZ, TUNE, VOID } from '../constants';
import { HazardKind } from '../events';
import { Cause, MapId } from '../ids';
import { DEG, TAU, wrapAngle } from '../math';
import { rand, randInt } from '../rng';
import { isPredicting } from '../sim/world';
import type { Vec2, World } from '../types';
import { MAP_TEMPO, beatAtTick, createTempo, roundTempo, tickAtBeat, type Tempo } from './clock';
import { addTelegraph, capsuleSolid, circleSolid, neutralHazardPose, progress, seededStream, solidDistance } from './common';
import { inDisc, nearestOnDisc } from './geometry';
import { HazardKey, emitFire, emitWarn, hazardKick, hazardNudge, hitEdgeBeans } from './hazards';
import { SolidTag, type HazardPose, type MapDef, type Solid } from './mapTypes';
import { datan2, dcos, dsin } from '../sim/dmath';

export const TUNING = {
  floorRadius: 7.0,
  hub: { radius: 0.9, top: 1.0 },
  /**
   * Spawns at radius 4.5 (edge safety 2.5 m), two per quadrant at 25° from the axes. The arms start
   * on an axis (θ0 is a seeded quarter turn), so every spawn is ≥ 1.5 m from both arms at BONK
   * (§6.5's 22.5° + k·45° would leave 1.47 m; WP2 decisions).
   */
  spawnRadius: 4.5,
  spawnDeg: [25, 65, 115, 155, 205, 245, 295, 335],
  arm: {
    r0: 0.9, r1: 7.6, radius: 0.25, top: 0.55, restitution: 0.2,
    /** Bars per revolution: Calm, Ramp, Sudden Death (at the SD tempo). */
    revBarsCalm: 4, revBarsRamp: 2, revBarsSD: 2,
    /** Sudden Death: direction reverses every this many SD bars, warned this many beats ahead. */
    reverseEveryBars: 4, reverseWarnBeats: 2,
    /** The third arm's shadow telegraph (bars before Sudden Death) before it lands at θ + 90°. */
    thirdArmDropBars: 1, thirdArmOffsetDeg: 90,
    /** θ0 is a seeded multiple of this (degrees). */
    startStepDeg: 90,
  },
  sweep: { tangentialMult: 1.1, radialSpeed: 2, dizzy: 6, dizzyEveryTicks: 30 },
  bubble: {
    slots: 6, radius: 0.6, pathRadius: 5, maxRadius: 6,
    /** Lissajous angular frequencies (rad/s) are seeded in this range, at least `minFreqGap` apart. */
    freqMin: 0.22, freqMax: 0.5, minFreqGap: 0.06,
    /** Pop cycle per slot in bars (Calm, Ramp on), slots staggered by one bar. */
    cycleBarsCalm: 8, cycleBarsRamp: 4,
    wobbleBeats: 2, growTicks: 30,
    popRadius: 1.5, popSpeed: 4, popMaxZ: 2.0,
  },
  /** Bot danger: the time resolution of arm prediction (ticks) and the bubble danger level. */
  danger: { armStepTicks: 2, bubble: 0.5 },
} as const;

const R = TUNING.floorRadius;
const ARM = TUNING.arm;
const BUB = TUNING.bubble;
const BEATS_PER_BAR = TUNE.clock.beatsPerBar;

const HUB = circleSolid(0, 0, TUNING.hub.radius, TUNING.hub.top, TUNE.body.restitution, SolidTag.NONE, 0);
const ARM_SOLIDS: Solid[] = [0, 1, 2].map((i) => capsuleSolid(0, 0, 0, 0, ARM.radius, ARM.top, ARM.restitution, SolidTag.SWEEPER, i));

const tempo = createTempo();

export function spinTempo(w: World): Tempo {
  return roundTempo(w.cfg.mapId, w.cfg.modeId, w.cfg.capTickOverride, tempo);
}

// ---- Sweeper arms -------------------------------------------------------------------------------

let startSeed = NaN;
let startAngle = 0;

/** Seeded starting angle θ0 of arm 0: a quarter turn, so the arms start between spawn pairs. */
export function sweeperStartAngle(seed: number): number {
  if (seed !== startSeed) {
    const steps = Math.round(360 / ARM.startStepDeg);
    startAngle = randInt(seededStream(seed, 1), steps) * ARM.startStepDeg * DEG;
    startSeed = seed;
  }
  return startAngle;
}

/** Ticks in one Sudden Death reversal segment (4 SD bars). */
export function reverseSegTicks(t: Tempo): number {
  return ARM.reverseEveryBars * BEATS_PER_BAR * t.tpbSD;
}

function omegaCalm(t: Tempo): number {
  return TAU / (ARM.revBarsCalm * BEATS_PER_BAR * t.tpb);
}
function omegaRamp(t: Tempo): number {
  return TAU / (ARM.revBarsRamp * BEATS_PER_BAR * t.tpb);
}
function omegaSD(t: Tempo): number {
  return TAU / (ARM.revBarsSD * BEATS_PER_BAR * t.tpbSD);
}

/**
 * Angle of arm 0 at a (fractional) round tick: θ0 until BONK, then piecewise-linear at the Calm,
 * Ramp and Sudden Death speeds, with the SD direction alternating every reversal segment.
 */
function baseAngle(seed: number, tick: number, t: Tempo): number {
  const theta0 = sweeperStartAngle(seed);
  if (tick <= 0) return theta0;
  let a = theta0 + omegaCalm(t) * Math.min(tick, t.rampStart);
  if (tick <= t.rampStart) return a;
  a += omegaRamp(t) * (Math.min(tick, t.sdStart) - t.rampStart);
  if (tick <= t.sdStart) return a;
  const seg = reverseSegTicks(t);
  const tau = tick - t.sdStart;
  const k = Math.floor(tau / seg);
  const within = tau - k * seg;
  const w = omegaSD(t);
  // Completed segments alternate +seg/−seg, so only an odd count leaves one forward segment.
  return a + (k % 2 === 1 ? w * seg : 0) + (k % 2 === 0 ? 1 : -1) * w * within;
}

/** Direction of rotation at `tick` (+1 increasing angle, −1 decreasing). */
export function sweeperDir(tick: number, t: Tempo): number {
  if (tick < t.sdStart) return 1;
  return Math.floor((tick - t.sdStart) / reverseSegTicks(t)) % 2 === 0 ? 1 : -1;
}

/** Signed angular speed of the arms in rad/s at `tick` (0 before BONK). */
export function sweeperOmega(tick: number, t: Tempo): number {
  if (tick < 0) return 0;
  const perTick = tick < t.rampStart ? omegaCalm(t) : tick < t.sdStart ? omegaRamp(t) : omegaSD(t);
  return sweeperDir(tick, t) * perTick * TICK_HZ;
}

/** Angle of arm `armIndex` (0, 1: the pair; 2: the SD third arm at +90°) at a fractional tick. */
export function sweeperAngle(seed: number, tick: number, armIndex: number, t: Tempo): number {
  const base = baseAngle(seed, tick, t);
  if (armIndex === 1) return base + Math.PI;
  if (armIndex === 2) return base + ARM.thirdArmOffsetDeg * DEG;
  return base;
}

/** Tick at which the third arm's shadow appears (1 bar before Sudden Death). */
export function thirdArmShadowTick(t: Tempo): number {
  return tickAtBeat(t, t.sdBeat - ARM.thirdArmDropBars * BEATS_PER_BAR);
}

/** Tick at which the third arm lands and starts sweeping: the Sudden Death downbeat. */
export function thirdArmLandTick(t: Tempo): number {
  return t.sdStart;
}

/** Number of arms that sweep (are solid) at `tick`. */
export function activeArms(tick: number, t: Tempo): number {
  return tick >= thirdArmLandTick(t) ? 3 : 2;
}

/** The `n`-th reversal tick (n ≥ 1). */
export function reversalTick(n: number, t: Tempo): number {
  return t.sdStart + n * reverseSegTicks(t);
}

/** Tick at which the warning of the `n`-th reversal starts (2 SD beats ahead). */
export function reversalWarnTick(n: number, t: Tempo): number {
  return reversalTick(n, t) - ARM.reverseWarnBeats * t.tpbSD;
}

function setArmSolid(s: Solid, angle: number, omega: number): void {
  const c = dcos(angle);
  const sn = dsin(angle);
  s.ax = c * ARM.r0;
  s.ay = sn * ARM.r0;
  s.bx = c * ARM.r1;
  s.by = sn * ARM.r1;
  s.omega = omega;
  s.cx = 0;
  s.cy = 0;
}

/** Fills the scratch arm solids for `tick`; returns how many are active. */
function updateArms(w: World, tick: number, t: Tempo): number {
  const n = activeArms(tick, t);
  const omega = sweeperOmega(tick, t);
  for (let i = 0; i < n; i++) setArmSolid(ARM_SOLIDS[i], sweeperAngle(w.cfg.seed, tick, i, t), omega);
  return n;
}

// ---- Bubbles ------------------------------------------------------------------------------------

interface BubblePath { a: number; b: number; phi: number; psi: number }

let pathSeed = -1;
const paths: BubblePath[] = Array.from({ length: BUB.slots }, () => ({ a: 0, b: 0, phi: 0, psi: 0 }));

/** Seeded Lissajous parameters per slot (cached for the last seed asked). */
function bubblePaths(seed: number): readonly BubblePath[] {
  if (seed === pathSeed) return paths;
  for (let i = 0; i < BUB.slots; i++) {
    const h = seededStream(seed, 100 + i);
    const p = paths[i];
    p.a = BUB.freqMin + rand(h) * (BUB.freqMax - BUB.freqMin);
    do p.b = BUB.freqMin + rand(h) * (BUB.freqMax - BUB.freqMin);
    while (Math.abs(p.a - p.b) < BUB.minFreqGap);
    p.phi = rand(h) * TAU;
    p.psi = rand(h) * TAU;
  }
  pathSeed = seed;
  return paths;
}

/** Bubble `slot`'s path point at a (fractional) tick: 5·(sin(a·t + φ), sin(b·t + ψ)), clamped to r ≤ 6. */
export function bubblePos(seed: number, slot: number, tick: number, out: Vec2): Vec2 {
  const p = bubblePaths(seed)[slot];
  const s = tick / TICK_HZ;
  let x = BUB.pathRadius * dsin(p.a * s + p.phi);
  let y = BUB.pathRadius * dsin(p.b * s + p.psi);
  const r = Math.hypot(x, y);
  if (r > BUB.maxRadius) {
    x *= BUB.maxRadius / r;
    y *= BUB.maxRadius / r;
  }
  out.x = x;
  out.y = y;
  return out;
}

/** True when bubble `slot` pops at the start of bar `bar` (8-bar cycle in Calm, 4 from Ramp, staggered by 1 bar). */
export function isBubblePopBar(slot: number, bar: number, t: Tempo): boolean {
  if (bar < 1) return false;
  const cycle = bar < t.rampBeat / BEATS_PER_BAR ? BUB.cycleBarsCalm : BUB.cycleBarsRamp;
  return (((bar - 1 - slot) % cycle) + cycle) % cycle === 0;
}

/** The first pop bar of `slot` after bar `bar`. */
function nextPopBar(slot: number, bar: number, t: Tempo): number {
  let b = Math.max(bar + 1, 1);
  while (!isBubblePopBar(slot, b, t)) b++;
  return b;
}

/** The last pop bar of `slot` at or before bar `bar`, or −Infinity. */
function lastPopBar(slot: number, bar: number, t: Tempo): number {
  for (let b = bar; b >= 1 && b > bar - BUB.cycleBarsCalm; b--) if (isBubblePopBar(slot, b, t)) return b;
  return -Infinity;
}

function barTick(bar: number, t: Tempo): number {
  return tickAtBeat(t, bar * BEATS_PER_BAR);
}

/** Index of the bar containing a (fractional) tick. */
function barAt(tick: number, t: Tempo): number {
  return Math.floor(beatAtTick(t, tick) / BEATS_PER_BAR);
}

/** Tick of the next pop of `slot` after the bar containing `tick`. */
export function nextBubblePopTick(slot: number, tick: number, t: Tempo): number {
  return barTick(nextPopBar(slot, barAt(tick, t), t), t);
}

/** Tick at which the wobble of the pop at `popBar` starts (`wobbleBeats` before it). */
function bubbleWarnTick(popBar: number, t: Tempo): number {
  return tickAtBeat(t, popBar * BEATS_PER_BAR - BUB.wobbleBeats);
}

// ---- Danger forecast ----------------------------------------------------------------------------

/**
 * The arm angles over the lookahead and the bubble pops inside it, for one (world, tick, lookahead).
 * `danger()` is sampled on whole grids per tick, and this is a pure function of (seed, tick), so it
 * is computed once per tick and reused.
 */
interface Forecast { w: World | null; tick: number; ahead: number; steps: number; arms: Int8Array; angles: Float64Array; pops: number; popX: Float64Array; popY: Float64Array }
const MAX_FORECAST_STEPS = 128;
const fc: Forecast = {
  w: null, tick: NaN, ahead: -1, steps: 0,
  arms: new Int8Array(MAX_FORECAST_STEPS), angles: new Float64Array(MAX_FORECAST_STEPS * 3),
  pops: 0, popX: new Float64Array(BUB.slots), popY: new Float64Array(BUB.slots),
};

function forecast(w: World, aheadTicks: number): Forecast {
  if (fc.w === w && fc.tick === w.tick && fc.ahead === aheadTicks) return fc;
  const t = spinTempo(w);
  const step = TUNING.danger.armStepTicks;
  fc.w = w;
  fc.tick = w.tick;
  fc.ahead = aheadTicks;
  fc.steps = Math.min(MAX_FORECAST_STEPS, Math.floor(aheadTicks / step) + 1);
  for (let k = 0; k < fc.steps; k++) {
    const tick = Math.max(0, w.tick + k * step);
    const n = activeArms(tick, t);
    fc.arms[k] = n;
    for (let i = 0; i < n; i++) fc.angles[k * 3 + i] = sweeperAngle(w.cfg.seed, tick, i, t);
  }
  fc.pops = 0;
  for (let i = 0; i < BUB.slots; i++) {
    const pop = nextBubblePopTick(i, w.tick, t);
    if (pop - w.tick > aheadTicks) continue;
    bubblePos(w.cfg.seed, i, pop, pt);
    fc.popX[fc.pops] = pt.x;
    fc.popY[fc.pops] = pt.y;
    fc.pops++;
  }
  return fc;
}

// ---- Pose, events, sweep, danger ----------------------------------------------------------------

const pt: Vec2 = { x: 0, y: 0 };

/** The third arm's telegraph point: halfway along it. */
function thirdArmMark(seed: number, tick: number, t: Tempo, out: Vec2): Vec2 {
  const a = sweeperAngle(seed, tick, 2, t);
  out.x = dcos(a) * R * 0.5;
  out.y = dsin(a) * R * 0.5;
  return out;
}

function writePose(w: World, tick: number, out: HazardPose): HazardPose {
  neutralHazardPose(out);
  const t = spinTempo(w);
  const seed = w.cfg.seed;
  const shadow = thirdArmShadowTick(t);
  const land = thirdArmLandTick(t);
  out.armCount = tick >= shadow ? 3 : 2;
  for (let i = 0; i < out.armCount; i++) out.armAngles[i] = sweeperAngle(seed, tick, i, t);
  out.armDir = sweeperDir(tick, t);
  out.armOmega = sweeperOmega(tick, t);
  out.armDrop = tick >= shadow ? progress(tick, shadow, land) : 0;
  if (tick >= shadow && tick < land) {
    thirdArmMark(seed, tick, t, pt);
    addTelegraph(out, HazardKind.THIRD_ARM, 2, shadow, land, pt.x, pt.y);
  }
  if (tick >= t.sdStart - ARM.reverseWarnBeats * t.tpbSD) {
    const n = Math.max(1, Math.floor((tick - t.sdStart) / reverseSegTicks(t)) + 1);
    const rev = reversalTick(n, t);
    const warn = reversalWarnTick(n, t);
    if (tick >= warn) {
      out.reverseWarn = progress(tick, warn, rev);
      addTelegraph(out, HazardKind.SWEEPER_REVERSE, n, warn, rev, 0, 0);
    }
  }
  const bar = barAt(tick, t);
  for (let i = 0; i < BUB.slots; i++) {
    const bub = out.bubbles[i];
    bubblePos(seed, i, tick, pt);
    bub.x = pt.x;
    bub.y = pt.y;
    const last = lastPopBar(i, bar, t);
    bub.scale = last === -Infinity ? 1 : Math.min(1, (tick - barTick(last, t)) / BUB.growTicks);
    const next = nextPopBar(i, bar, t);
    const popTick = barTick(next, t);
    const warnTick = bubbleWarnTick(next, t);
    bub.wobble = progress(tick, warnTick, popTick);
    if (tick >= warnTick) {
      bubblePos(seed, i, popTick, pt);
      addTelegraph(out, HazardKind.BUBBLE, i, warnTick, popTick, pt.x, pt.y);
    }
  }
  return out;
}

/** Time-derived hazard events at integer tick `w.tick` (both modes); bubble pops also nudge. */
function emitTelegraphs(w: World, t: Tempo): void {
  const tick = w.tick;
  const seed = w.cfg.seed;
  const shadow = thirdArmShadowTick(t);
  const land = thirdArmLandTick(t);
  if (tick === shadow) emitWarn(w, HazardKind.THIRD_ARM, 2, land - shadow, thirdArmMark(seed, tick, t, pt).x, pt.y);
  else if (tick === land) emitFire(w, HazardKind.THIRD_ARM, 2, thirdArmMark(seed, tick, t, pt).x, pt.y);
  const lead = ARM.reverseWarnBeats * t.tpbSD;
  if (tick >= t.sdStart) {
    const seg = reverseSegTicks(t);
    const tau = tick - t.sdStart;
    if (tau > 0 && tau % seg === 0) emitFire(w, HazardKind.SWEEPER_REVERSE, tau / seg, 0, 0);
    if ((tau + lead) % seg === 0) emitWarn(w, HazardKind.SWEEPER_REVERSE, (tau + lead) / seg, lead, 0, 0);
  }
  if (tick < 0) return;
  const bar = Math.round(beatAtTick(t, tick) / BEATS_PER_BAR);
  const onBar = barTick(bar, t) === tick;
  const curBar = barAt(tick, t);
  for (let i = 0; i < BUB.slots; i++) {
    if (onBar && isBubblePopBar(i, bar, t)) {
      bubblePos(seed, i, tick, pt);
      emitFire(w, HazardKind.BUBBLE, i, pt.x, pt.y);
      hazardNudge(w, pt.x, pt.y, BUB.popRadius, BUB.popSpeed, 0, Cause.BUBBLE, BUB.popMaxZ);
    }
    const next = nextPopBar(i, curBar, t);
    const warnTick = bubbleWarnTick(next, t);
    if (warnTick === tick) {
      const popTick = barTick(next, t);
      bubblePos(seed, i, popTick, pt);
      emitWarn(w, HazardKind.BUBBLE, i, popTick - tick, pt.x, pt.y);
    }
  }
}

/**
 * Sweep response (§6.5): a grounded body touched by an arm's leading side takes the arm's carry
 * velocity `1.1·|ω|·r` tangential plus 2 m/s radially outward, or keeps its own speed along that
 * direction when it is already faster (`max(own, carry)`, own measured after the contact's own
 * restitution push), +6 Dizzy at most once per 30 ticks, TUMBLE above 6 m/s. Trailing-side
 * contact only separates (the arm is moving away).
 */
function sweep(w: World, slot: number, s: Solid, nx: number, ny: number): void {
  const b = w.beans[slot];
  const r = Math.hypot(b.pos.x, b.pos.y);
  if (r < 1e-6 || s.omega === 0) return;
  const rx = b.pos.x / r;
  const ry = b.pos.y / r;
  const sign = s.omega > 0 ? 1 : -1;
  const tx = -ry * sign;
  const ty = rx * sign;
  if (nx * tx + ny * ty <= 0) return;
  const vt = TUNING.sweep.tangentialMult * Math.abs(s.omega) * r;
  const vx = tx * vt + rx * TUNING.sweep.radialSpeed;
  const vy = ty * vt + ry * TUNING.sweep.radialSpeed;
  const carry = Math.hypot(vx, vy);
  const speed = Math.max(carry, (b.vel.x * vx + b.vel.y * vy) / carry);
  hazardKick(w, slot, vx, vy, speed, {
    cause: Cause.SWEEPER, dizzy: TUNING.sweep.dizzy, scaleByDizzy: false,
    key: HazardKey.SWEEPER, cooldown: TUNING.sweep.dizzyEveryTicks, carry: true,
    noTumble: speed <= TUNE.knock.tumbleThreshold,
  });
}

export const SPIN: MapDef = {
  id: MapId.SPIN,
  name: 'Spin Cycle',
  tpb: MAP_TEMPO[MapId.SPIN].tpb,
  tpbSD: MAP_TEMPO[MapId.SPIN].tpbSD,
  bounds: { minX: -R, maxX: R, minY: -R, maxY: R },
  spawns: TUNING.spawnDeg.map((deg) => ({ x: TUNING.spawnRadius * dcos(deg * DEG), y: TUNING.spawnRadius * dsin(deg * DEG) })),
  voidKind: 'foam',
  center: { x: 0, y: 0 },
  initState() {},
  groundAt(_w, x, y) {
    return inDisc(x, y, 0, 0, R) ? 0 : VOID;
  },
  nearestGround(_w, x, y, out) {
    return nearestOnDisc(x, y, 0, 0, R, out);
  },
  solids(w, out) {
    out[0] = HUB;
    const n = updateArms(w, w.tick, spinTempo(w));
    for (let i = 0; i < n; i++) out[1 + i] = ARM_SOLIDS[i];
    return 1 + n;
  },
  onSolidContact(w, id, isEntity, s, nx, ny) {
    if (!isEntity && s.tag === SolidTag.SWEEPER) sweep(w, id, s, nx, ny);
  },
  step(w) {
    const t = spinTempo(w);
    emitTelegraphs(w, t);
    if (w.tick <= 0 || isPredicting(w)) return;
    const n = updateArms(w, w.tick, t);
    for (let i = 0; i < n; i++) hitEdgeBeans(w, ARM_SOLIDS[i], Cause.SWEEPER);
  },
  danger(w, x, y, aheadTicks) {
    const f = forecast(w, aheadTicks);
    let d = 0;
    const r = Math.hypot(x, y);
    const reach = ARM.radius + TUNE.bean.radius;
    if (r >= ARM.r0 - reach && r <= ARM.r1 + reach) {
      const phi = datan2(y, x);
      const half = datan2(reach, Math.max(r, reach));
      const step = TUNING.danger.armStepTicks;
      for (let k = 0; k < f.steps && d === 0; k++) {
        for (let i = 0; i < f.arms[k]; i++) {
          if (Math.abs(wrapAngle(f.angles[k * 3 + i] - phi)) <= half) {
            d = 1 - (k * step) / (aheadTicks + step);
            break;
          }
        }
      }
    }
    const reach2 = (BUB.popRadius + TUNE.bean.radius) ** 2;
    for (let i = 0; i < f.pops; i++) {
      if ((x - f.popX[i]) ** 2 + (y - f.popY[i]) ** 2 <= reach2) d = Math.max(d, TUNING.danger.bubble);
    }
    return d;
  },
  hazardPose(w, tick, out) {
    return writePose(w, tick, out);
  },
  crateExclusion(w, x, y) {
    if (solidDistance(HUB, x, y) <= TUNE.crate.radius) return true;
    const n = updateArms(w, w.tick, spinTempo(w));
    for (let i = 0; i < n; i++) if (solidDistance(ARM_SOLIDS[i], x, y) <= TUNE.crate.radius) return true;
    return false;
  },
};
